import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaSave,
  FaCheckCircle,
  FaFileAlt,
  FaImage,
  FaGlobe,
  FaUser,
  FaClock,
  FaFolder,
  FaEye,
  FaMagic,
  FaTrashAlt,
  FaExclamationCircle,
  FaPlus,
} from 'react-icons/fa';
import slugify from 'slugify';
import { useBlogForm } from './useBlogForm';
import RichTextEditor from '../../ui/RichTextEditor';
import TagsInput from '../../ui/TagsInput';
import { apiFetch } from '../../../utils/api';

const DEFAULT_CATEGORY_OPTIONS = [
  'Financial Advisory',
  'Corporate Law & Compliance',
  'Tax & Auditing',
  'FinTech & Banking',
  'Business Strategy',
  'Legal Solutions',
  'Technology & Innovation',
];

export default function BlogForm({ onSubmit, initialData = {} }) {
  const {
    formData,
    setFormData,
    handleInputChange,
    handleTitleChange,
    handleContentChange,
    handleTagsChange,
    handleImageChange,
    handleSubmit,
    isSubmitting,
    errors,
  } = useBlogForm({ onSubmit, initialData });

  const [categories, setCategories] = useState(DEFAULT_CATEGORY_OPTIONS);
  const [isEditingSlug, setIsEditingSlug] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        const res = await apiFetch('/api/categories');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0 && isMounted) {
            setCategories(data.map((c) => (typeof c === 'string' ? c : c.name)));
          }
        }
      } catch (e) {
        // Fallback to DEFAULT_CATEGORY_OPTIONS
      }
    };
    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  // Live text calculations
  const plainText = (formData.content || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const wordCount = plainText ? plainText.split(/\s+/).length : 0;

  // Character limit calculations
  const titleLen = formData.title?.length || 0;
  const isTitleExceeded = titleLen > 120;

  const shortDescLen = formData.shortDescription?.length || 0;
  const isShortDescExceeded = shortDescLen > 250;

  const metaTitleLen = formData.metaTitle?.length || 0;
  const isMetaTitleExceeded = metaTitleLen > 60;

  const metaDescLen = formData.metaDescription?.length || 0;
  const isMetaDescExceeded = metaDescLen > 160;

  const handleRegenerateSlug = () => {
    if (formData.title) {
      setFormData((prev) => ({
        ...prev,
        slug: slugify(prev.title, { lower: true, strict: true }),
      }));
    }
  };

  const handleSaveAsDraft = (e) => {
    setFormData((prev) => ({ ...prev, status: 'Draft' }));
    handleSubmit(e, { status: 'Draft' });
  };

  const handlePublishNow = (e) => {
    setFormData((prev) => ({ ...prev, status: 'Published' }));
    handleSubmit(e, { status: 'Published' });
  };

  return (
    <form onSubmit={(e) => handleSubmit(e)} className="space-y-5">
      {/* Top Validation Error Banner */}
      {errors.form && (
        <div className="flex items-center gap-3 rounded-xl border border-red-300 bg-red-50 p-3.5 text-xs font-semibold text-red-800 shadow-2xs">
          <FaExclamationCircle className="text-base text-red-600 flex-shrink-0" />
          <span>{errors.form}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 items-start">
        {/* ================= LEFT MAIN COLUMN (8 cols) ================= */}
        <div className="space-y-4 lg:col-span-8">
          {/* 1. Title & Slug Card */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs">
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="blog-title" className="block text-xs font-semibold text-slate-700">
                    Blog Title <span className="text-red-500">*</span>
                  </label>
                  <span
                    className={`text-[11px] font-medium transition ${
                      isTitleExceeded ? 'text-red-600 font-bold' : 'text-slate-400'
                    }`}
                  >
                    {titleLen}/120 chars {isTitleExceeded && `(Exceeded by ${titleLen - 120})`}
                  </span>
                </div>
                <input
                  id="blog-title"
                  type="text"
                  name="title"
                  placeholder="Enter blog title..."
                  value={formData.title}
                  onChange={handleTitleChange}
                  className={`w-full rounded-lg border px-3.5 py-2 text-sm font-medium text-slate-900 transition placeholder:text-slate-400 focus:outline-none focus:ring-1 ${
                    errors.title || isTitleExceeded
                      ? 'border-red-400 bg-red-50/20 focus:ring-red-200'
                      : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 focus:ring-amber-200'
                  }`}
                  required
                />
                {errors.title && <p className="mt-1 text-xs text-red-600 font-medium">{errors.title}</p>}
              </div>

              {/* Slug Permalink Bar */}
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 border border-slate-200/60 px-3 py-1.5 text-xs text-slate-600">
                <span className="font-medium text-slate-600">Permalink:</span>
                <span className="text-slate-400 font-mono">/blogs/</span>
                {isEditingSlug ? (
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    className="rounded border border-slate-300 bg-white px-2 py-0.5 font-mono text-xs text-slate-800 focus:border-amber-400 focus:outline-none"
                  />
                ) : (
                  <span className="font-mono font-medium text-slate-800">{formData.slug || 'slug-auto-generated'}</span>
                )}

                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditingSlug(!isEditingSlug)}
                    className="font-medium text-amber-700 hover:text-amber-800 hover:underline"
                  >
                    {isEditingSlug ? 'Done' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    onClick={handleRegenerateSlug}
                    title="Regenerate slug from title"
                    className="flex items-center gap-1 text-slate-500 hover:text-slate-800"
                  >
                    <FaMagic className="text-[10px]" /> Reset
                  </button>
                </div>
              </div>
              {errors.slug && <p className="mt-1 text-xs text-red-600 font-medium">{errors.slug}</p>}
            </div>
          </div>

          {/* 2. Short Description / Excerpt Card */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs">
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="short-desc" className="block text-xs font-semibold text-slate-700">
                Short Description / Excerpt <span className="text-red-500">*</span>
              </label>
              <span
                className={`text-[11px] font-medium transition ${
                  isShortDescExceeded ? 'text-red-600 font-bold' : 'text-slate-400'
                }`}
              >
                {shortDescLen}/250 chars {isShortDescExceeded && `(Exceeded by ${shortDescLen - 250})`}
              </span>
            </div>
            <textarea
              id="short-desc"
              name="shortDescription"
              rows={2}
              placeholder="Brief summary that appears on blog preview cards..."
              value={formData.shortDescription}
              onChange={handleInputChange}
              className={`w-full rounded-lg border p-3 text-xs sm:text-sm text-slate-800 transition placeholder:text-slate-400 focus:outline-none focus:ring-1 ${
                errors.shortDescription || isShortDescExceeded
                  ? 'border-red-400 bg-red-50/20 focus:ring-red-200'
                  : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 focus:ring-amber-200'
              }`}
              required
            />
            {errors.shortDescription && <p className="mt-1 text-xs text-red-600 font-medium">{errors.shortDescription}</p>}
          </div>

          {/* 3. Article Content (Rich Text Editor) */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <label className="block text-xs font-semibold text-slate-700">
                  Article Body Content <span className="text-red-500">*</span>
                </label>
                <p className="text-[11px] text-slate-400">Format text, add headers, links, lists, and images.</p>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <span className="rounded-md bg-slate-100 px-2 py-0.5 font-medium">{wordCount} words</span>
                <span className="rounded-md bg-slate-100 px-2 py-0.5 font-medium">{plainText.length} chars</span>
              </div>
            </div>

            <div className="mt-2">
              <RichTextEditor value={formData.content} onChange={handleContentChange} />
            </div>
            {errors.content && <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.content}</p>}
          </div>

          {/* 4. SEO & Metadata Card */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs">
            <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-2.5">
              <FaGlobe className="text-amber-500 text-sm" />
              <h3 className="text-xs sm:text-sm font-semibold text-slate-800">Search Engine Optimization (SEO)</h3>
            </div>

            <div className="space-y-3">
              {/* Meta Title */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="meta-title" className="block text-xs font-semibold text-slate-700">
                    Meta Title <span className="text-red-500">*</span>
                  </label>
                  <span
                    className={`text-[11px] font-medium transition ${
                      isMetaTitleExceeded ? 'text-red-600 font-bold' : 'text-slate-400'
                    }`}
                  >
                    {metaTitleLen}/60 chars {isMetaTitleExceeded && `(Exceeded by ${metaTitleLen - 60})`}
                  </span>
                </div>
                <input
                  id="meta-title"
                  type="text"
                  name="metaTitle"
                  placeholder="SEO title that appears on search engines (max 60 chars)..."
                  value={formData.metaTitle}
                  onChange={handleInputChange}
                  className={`w-full rounded-lg border px-3 py-2 text-xs sm:text-sm text-slate-800 transition focus:outline-none focus:ring-1 ${
                    errors.metaTitle || isMetaTitleExceeded
                      ? 'border-red-400 bg-red-50/20 focus:ring-red-200'
                      : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 focus:ring-amber-200'
                  }`}
                  required
                />
                {errors.metaTitle && <p className="mt-1 text-xs text-red-600 font-medium">{errors.metaTitle}</p>}
              </div>

              {/* Meta Description */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="meta-desc" className="block text-xs font-semibold text-slate-700">
                    Meta Description <span className="text-red-500">*</span>
                  </label>
                  <span
                    className={`text-[11px] font-medium transition ${
                      isMetaDescExceeded ? 'text-red-600 font-bold' : 'text-slate-400'
                    }`}
                  >
                    {metaDescLen}/160 chars {isMetaDescExceeded && `(Exceeded by ${metaDescLen - 160})`}
                  </span>
                </div>
                <textarea
                  id="meta-desc"
                  name="metaDescription"
                  rows={2}
                  placeholder="Concise snippet that search engines display under the title (max 160 chars)..."
                  value={formData.metaDescription}
                  onChange={handleInputChange}
                  className={`w-full rounded-lg border px-3 py-2 text-xs sm:text-sm text-slate-800 transition focus:outline-none focus:ring-1 ${
                    errors.metaDescription || isMetaDescExceeded
                      ? 'border-red-400 bg-red-50/20 focus:ring-red-200'
                      : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 focus:ring-amber-200'
                  }`}
                  required
                />
                {errors.metaDescription && <p className="mt-1 text-xs text-red-600 font-medium">{errors.metaDescription}</p>}
              </div>

              {/* Google SERP Snippet Preview */}
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Google Search Snippet Preview</p>
                <div className="space-y-0.5">
                  <p className="text-[11px] text-slate-500 truncate">
                    https://finegaltech.com/blogs/{formData.slug || 'article-slug'}
                  </p>
                  <p className={`text-xs sm:text-sm font-medium hover:underline cursor-pointer line-clamp-1 ${isMetaTitleExceeded ? 'text-red-600' : 'text-blue-700'}`}>
                    {formData.metaTitle || formData.title || 'Your Article Title Appears Here | Finegal Tech Solution'}
                  </p>
                  <p className={`text-xs line-clamp-2 ${isMetaDescExceeded ? 'text-red-600' : 'text-slate-600'}`}>
                    {formData.metaDescription || formData.shortDescription || 'Search snippet preview shows what users will read when this article ranks on Google search results.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ================= RIGHT SIDEBAR COLUMN (4 cols) ================= */}
        <div className="space-y-4 lg:col-span-4 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto no-scrollbar">
          {/* Card 1: Publish & Status Controls */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs">
            <h3 className="mb-3 text-xs sm:text-sm font-semibold text-slate-800">Publishing Details</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Publication Status
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, status: 'Draft' }))}
                    className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-semibold transition ${
                      formData.status === 'Draft'
                        ? 'border-amber-400 bg-amber-50 text-amber-900 shadow-2xs'
                        : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <FaFileAlt className="text-[11px]" /> Draft
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, status: 'Published' }))}
                    className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-semibold transition ${
                      formData.status === 'Published'
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-900 shadow-2xs'
                        : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <FaCheckCircle className="text-[11px]" /> Published
                  </button>
                </div>
              </div>

              {/* Reading Time */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="reading-time" className="block text-xs font-semibold text-slate-700">
                    Reading Time
                  </label>
                  <span className="text-[11px] text-slate-400">minutes</span>
                </div>
                <div className="relative">
                  <input
                    id="reading-time"
                    type="number"
                    name="readingTime"
                    min="1"
                    value={formData.readingTime}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pl-8 text-xs sm:text-sm text-slate-800 transition focus:border-amber-400 focus:bg-white focus:outline-none"
                    required
                  />
                  <FaClock className="absolute left-2.5 top-2.5 text-slate-400 text-xs" />
                </div>
                {errors.readingTime && <p className="mt-1 text-xs text-red-500">{errors.readingTime}</p>}
              </div>

              {/* Main Submit Buttons */}
              <div className="space-y-2 pt-1">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none disabled:opacity-60"
                >
                  <FaSave className="text-xs" />
                  {isSubmitting
                    ? 'Saving...'
                    : initialData?._id
                    ? 'Update Blog Article'
                    : formData.status === 'Published'
                    ? 'Publish Article'
                    : 'Save Article'}
                </button>

                {formData.status !== 'Published' ? (
                  <button
                    type="button"
                    onClick={handlePublishNow}
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-emerald-500 bg-emerald-50 py-2 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100"
                  >
                    <FaCheckCircle className="text-xs" /> Publish Immediately
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSaveAsDraft}
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-500 bg-amber-50 py-2 text-xs font-semibold text-amber-800 transition hover:bg-amber-100"
                  >
                    <FaFileAlt className="text-xs" /> Revert to Draft
                  </button>
                )}

                {initialData?.slug && (
                  <Link
                    to={`/admin/blogs/preview/${initialData.slug}`}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                  >
                    <FaEye className="text-xs" /> Preview in Browser
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Card 2: Featured Image Upload */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs">
            <div className="mb-2.5 flex items-center justify-between">
              <h3 className="text-xs sm:text-sm font-semibold text-slate-800">Featured Image</h3>
              <span className="text-[11px] text-slate-400">1200x630</span>
            </div>

            {formData.featuredImage ? (
              <div className="space-y-2">
                <div className="group relative overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                  <img
                    src={formData.featuredImage}
                    alt="Featured preview"
                    className="h-36 w-full object-cover transition duration-200 group-hover:scale-102"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => handleImageChange({ target: { files: [] } })}
                      className="flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm hover:bg-red-700"
                    >
                      <FaTrashAlt className="text-[10px]" /> Remove Image
                    </button>
                  </div>
                </div>

                <label className="flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-400 hover:bg-slate-50">
                  <span>Change Image</span>
                  <input
                    type="file"
                    name="featuredImage"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                  />
                </label>
              </div>
            ) : (
              <div>
                <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/60 p-5 text-center transition hover:border-amber-400 hover:bg-slate-50">
                  <div className="rounded-full bg-white p-2.5 shadow-2xs">
                    <FaImage className="text-lg text-amber-500" />
                  </div>
                  <p className="mt-2 text-xs font-semibold text-slate-700">Click to upload image</p>
                  <p className="mt-0.5 text-[10px] text-slate-400">PNG, JPG, WebP up to 5MB</p>
                  <input
                    type="file"
                    name="featuredImage"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                  />
                </label>
                {errors.featuredImage && <p className="mt-1 text-xs text-red-500">{errors.featuredImage}</p>}
              </div>
            )}
          </div>

          {/* Card 3: Categorization & Author */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs">
            <h3 className="mb-3 text-xs sm:text-sm font-semibold text-slate-800">Organization</h3>

            <div className="space-y-3">
              {/* Category */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="blog-category" className="block text-xs font-semibold text-slate-700">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <Link
                    to="/admin/categories"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] font-semibold text-amber-600 hover:text-amber-700 hover:underline flex items-center gap-1"
                    title="Open Categories manager to add/edit topics"
                  >
                    <FaPlus className="text-[9px]" /> Manage
                  </Link>
                </div>
                <div className="relative">
                  <select
                    id="blog-category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pl-8 text-xs sm:text-sm text-slate-800 transition focus:border-amber-400 focus:bg-white focus:outline-none"
                    required
                  >
                    <option value="">Select a Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  <FaFolder className="absolute left-2.5 top-2.5 text-slate-400 text-xs" />
                </div>
                {errors.category && <p className="mt-1 text-xs text-red-500">{errors.category}</p>}
              </div>

              {/* Author Name */}
              <div>
                <label htmlFor="author-name" className="block text-xs font-semibold text-slate-700 mb-1">
                  Author Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="author-name"
                    type="text"
                    name="authorName"
                    placeholder="e.g. Finegal Editorial Team"
                    value={formData.authorName}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pl-8 text-xs sm:text-sm text-slate-800 transition focus:border-amber-400 focus:bg-white focus:outline-none"
                    required
                  />
                  <FaUser className="absolute left-2.5 top-2.5 text-slate-400 text-xs" />
                </div>
                {errors.authorName && <p className="mt-1 text-xs text-red-500">{errors.authorName}</p>}
              </div>

              {/* Tags */}
              <div>
                <TagsInput tags={formData.tags} setTags={handleTagsChange} label="Tags & Keywords" />
                {errors.tags && <p className="mt-1 text-xs text-red-500">{errors.tags}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
